/**
 * Pricing helpers for the "Prebook vs Ride-Sharing" comparison.
 * All values come from the API in integer cents.
 *
 * Mirror of the plugin's resources/jsx/utils/pricing.js — keep the two in step.
 */

const breakdownOf = (car) => car?.pricing_breakdown || car?.line_by_line_breakdown;

/**
 * The price we advertise for a car = the pre-tax `net_total` row of the backend
 * breakdown (falls back to `total_price` when the breakdown has no such row).
 *
 * @param {Object} car
 * @returns {number} cents
 */
export function netTotalCents(car) {
    const bd = breakdownOf(car);
    const net = Array.isArray(bd) ? bd.find(b => b.key === 'net_total') : null;
    return (net && net.value != null) ? net.value : (car?.total_price ?? 0);
}

// A ride-share app gives you neither our prebook online discount nor a coupon, so
// the ride-share estimate is our own total with exactly these rows left out. Every
// other row (fare, airport fee, gratuity…) is a cost the trip has either way.
const RIDESHARE_EXCLUDED_KEYS = ['online_discount', 'discount_code'];

/**
 * Ride-share estimate = the same pre-tax total as netTotalCents, minus the online
 * and coupon discounts. Discount rows are negative, so subtracting them adds the
 * discounted amount back — and since
 *   net_total = fare + charges − discounts + gratuity,
 * this is exactly the plain sum of the breakdown's non-discount rows.
 *
 * DELIBERATE: the `gratuity` row is taken as the backend reports it, i.e. a
 * percentage of the ALREADY-DISCOUNTED subtotal. A coupon therefore shrinks the
 * gratuity too, and this estimate moves by that amount (a few dollars) when one is
 * applied. That is the agreed behaviour — the number must match a hand-sum of the
 * breakdown — so don't "fix" it by re-charging gratuity on the undiscounted subtotal.
 *
 * @param {Object} car
 * @returns {number} cents
 */
export function ridesharePriceCents(car) {
    const bd = breakdownOf(car);
    const base = netTotalCents(car);
    if (!Array.isArray(bd)) return base;
    return bd.reduce(
        (sum, line) => RIDESHARE_EXCLUDED_KEYS.includes(line.key) ? sum - (line.value || 0) : sum,
        base
    );
}

/**
 * Both figures of the compare row/modal, in whole dollars — always derived from the
 * same car so they can never drift apart.
 *
 * Our own price is CEILed (never quote below what the trip actually costs); the
 * ride-share figure is an estimate rendered with a "~", so it is ROUNDED — it has to
 * match a hand-sum of the breakdown ($180.20 reads as $180, not $181).
 *
 * @param {Object} car
 * @returns {{price: number, sharePrice: number}}
 */
export function comparePrices(car) {
    const price = Math.ceil(netTotalCents(car) / 100);
    // The two roundings differ, so a car with no discount rows (or no breakdown at
    // all, where both figures are the same total) could otherwise render a ride-share
    // estimate a dollar UNDER our own price — never show that in a compare row.
    return {price, sharePrice: Math.max(price, Math.round(ridesharePriceCents(car) / 100))};
}
